import { GS1_PRIMARY_KEY_AIS } from '@myplayer/gs1';
import {
  ROLES,
  type CapabilityName,
  type SpecCapabilityGroup,
  type WorkspacePersonaRole,
} from '@myplayer/rbac';
import {
  DESIGN_TOKENS,
  IconAlertCircle,
  IconAuditLog,
  IconCards,
  IconProducts,
  IconSettings,
  applyBrandThemeOverrides,
} from '@myplayer/ui';
import { useEffect, type ReactElement } from 'react';
import {
  BrowserRouter,
  NavLink,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { AdminTopNav } from './admin-top-nav.js';
import { AuditLogPage } from './audit-log-page.js';
import { AuthProvider, useAuth, type AdminUserSession } from './auth-context.js';
import {
  ChangePasswordPage,
  FirebaseAuthActionPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from './auth-screens.js';
import { Spinner } from './components/ui/primitives.js';
import type {
  ClearRecallCallablePayload,
  ClearRecallCallableResult,
  SetRecallCallablePayload,
  SetRecallCallableResult,
} from './firebase.js';
import { HelpPage } from './help/index.js';
import { OverviewPage } from './overview-page.js';
import { SettingsPage, SignInPage } from './pages.js';
import { ProductsPage } from './products-page.js';
import { openQrCheckInNewTab } from './qr-check-helper.js';
import { QrCheckPage } from './qr-check-page.js';
import {
  InsideAdminShellContext,
  RailActionMenu,
  resolveRailActionPageContext,
  useInsideAdminShell,
  type RailActionMenuProps,
  type RailActionPageContext,
} from './rail-action-menu.js';
import { ADMIN_STRINGS } from './strings.js';

export * from './audit-log-helpers.js';
export * from './auth-email-templates.js';
export * from './auth-screens.js';
export * from './help/index.js';
export * from './overview-analytics-helpers.js';
export * from './overview-page.js';
export * from './products-page.js';
export * from './qr-check-helper.js';
export * from './qr-check-page.js';
export * from './qr-customiser.js';
export * from './qr-generator.js';
export * from './qr-style-rules.js';
export { BrandQrSection, type BrandQrSectionProps } from './brand-qr-section.js';
export {
  AdminTopNav,
  InsideAdminShellContext,
  RailActionMenu,
  resolveRailActionPageContext,
  useInsideAdminShell,
  type RailActionMenuProps,
  type RailActionPageContext,
};
export { AuditLogPage, type AuditLogPageProps } from './audit-log-page.js';
export { DomainsPage } from './domains-page.js';
export { SettingsPage, SignInPage } from './pages.js';
export { ADMIN_STRINGS, formatPersonaRoleLabel, formatPlainAdminError } from './strings.js';

const INLINE_COLOR_VARS =
  '--color-page-bg,--color-surface,--color-surface-frosted,--color-surface-subtle,--color-surface-muted,--color-border,--color-border-medium,--color-border-subtle,--color-text-primary,--color-text-secondary,--color-text-muted,--color-brand,--color-brand-hover,--color-brand-wash,--color-brand-wash-hover,--color-brand-border-tint,--color-brand-contrast'.split(
    ',',
  );

export function syncSystemThemeWithBrandOverrides(): void {
  applyBrandThemeOverrides();
  if (typeof document === 'undefined') return;
  for (const prop of INLINE_COLOR_VARS) document.documentElement.style.removeProperty(prop);
}

export type AdminThemePreference = 'system' | 'light' | 'dark';

const THEME_STORAGE_KEY = 'mp_admin_theme';

export function applyThemePreference(pref: AdminThemePreference): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (pref === 'system') delete root.dataset['theme'];
  else root.dataset['theme'] = pref;
  root.classList.toggle('dark', pref === 'dark');
}

export function initializeAdminTheme(): void {
  syncSystemThemeWithBrandOverrides();
  if (typeof window === 'undefined') return;
  try {
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'dark' || saved === 'light') applyThemePreference(saved);
  } catch {
    // Ignore storage read errors in restricted environments
  }
}

export function getRoleChipVariant(
  personaRole: WorkspacePersonaRole,
): 'role_owner' | 'role_admin' | 'role_member' {
  if (personaRole === 'SUPER_ADMIN' || personaRole === 'ORG_ADMIN') return 'role_owner';
  if (personaRole === 'WORKSPACE_MANAGER' || personaRole === 'COMPLIANCE_OFFICER')
    return 'role_admin';
  return 'role_member';
}

export function getAdminConsoleMetadata() {
  return {
    title: 'MyPlayer Enterprise GS1 Digital Link Admin Console',
    supportedKeyCount: GS1_PRIMARY_KEY_AIS.length,
    roleCount: ROLES.length,
    primaryColor: DESIGN_TOKENS.colors.brand,
  };
}

export interface NavItemConfig {
  readonly id: 'overview' | 'products' | 'audit-log' | 'settings' | 'help';
  readonly label: string;
  readonly to: string;
  readonly readCapability: CapabilityName;
  readonly writeCapability: CapabilityName;
  readonly specCapability?: SpecCapabilityGroup;
}
export const ADMIN_NAV_ITEMS: readonly NavItemConfig[] = [
  {
    id: 'overview',
    label: 'Overview',
    to: '/overview',
    readCapability: 'identifier:read',
    writeCapability: 'identifier:create_stage_update',
    specCapability: 'createGtinsUploadCsvAndBindRoutes',
  },
  {
    id: 'products',
    label: 'Products',
    to: '/products',
    readCapability: 'identifier:read',
    writeCapability: 'identifier:create_stage_update',
    specCapability: 'createGtinsUploadCsvAndBindRoutes',
  },
  {
    id: 'audit-log',
    label: 'Audit Log',
    to: '/audit-log',
    readCapability: 'auditLog:read',
    writeCapability: 'auditLog:read',
  },
  {
    id: 'settings',
    label: 'Settings',
    to: '/settings',
    readCapability: 'domain:manage',
    writeCapability: 'workspace:update',
    specCapability: 'configureCustomDomainsAndConnectors',
  },
  {
    id: 'help',
    label: 'Help',
    to: '/help',
    readCapability: 'workspace:read',
    writeCapability: 'workspace:read',
  },
] as const;

const NAV_ICONS: Readonly<Record<NavItemConfig['id'], ReactElement>> = {
  overview: <IconCards />,
  products: <IconProducts />,
  'audit-log': <IconAuditLog />,
  settings: <IconSettings />,
  help: <IconAlertCircle />,
};

export function AdminShellLayout(): ReactElement {
  const {
    session,
    organisation,
    availableWorkspaces,
    workspaceScope,
    setWorkspaceScope,
    loading,
    signOut,
    switchWorkspace,
    switchPersonaRole,
    can,
    canSpec,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    syncSystemThemeWithBrandOverrides();
  }, []);

  if (loading) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="shell-loading-state"
        className="flex min-h-screen flex-col items-center justify-center gap-3 bg-page text-[14px] text-fg-secondary"
      >
        <Spinner size={28} />
        <span>{ADMIN_STRINGS.loading.workspaceSession}</span>
      </div>
    );
  }
  if (session === null) {
    return <Navigate to="/sign-in" state={{ from: location.pathname }} replace />;
  }

  const visibleNavItems = ADMIN_NAV_ITEMS.filter(
    (item) =>
      can(item.readCapability) || (item.specCapability ? canSpec(item.specCapability) : false),
  );
  const canCreateIdentifier =
    can('identifier:create_stage_update') || canSpec('createGtinsUploadCsvAndBindRoutes');
  const canExportIdentifier = can('analytics:export') || can('identifier:read');
  const canManageDomains = can('domain:manage') || canSpec('configureCustomDomainsAndConnectors');
  const canExportAuditLog = can('auditLog:read') && can('analytics:export');
  const pageContext = resolveRailActionPageContext(location.pathname);

  const dispatchOrNavigate = (eventName: string, targetPath: string): void => {
    if (location.pathname.startsWith('/products') && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(eventName));
    } else {
      navigate(targetPath);
    }
  };
  const dispatchWindowEvent = (eventName: string): void => {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(eventName));
  };

  return (
    <div className="flex min-h-dvh w-full max-w-full flex-col overflow-x-clip bg-page text-fg">
      <a
        href="#admin-main-content"
        data-testid="skip-to-main-link"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-control focus:border focus:border-brand focus:bg-surface focus:px-3 focus:py-2 focus:text-[13px] focus:font-semibold focus:text-fg"
      >
        {ADMIN_STRINGS.navigation.skipToMainContent}
      </a>

      <AdminTopNav
        session={session}
        organisation={organisation}
        availableWorkspaces={availableWorkspaces}
        workspaceScope={workspaceScope}
        setWorkspaceScope={setWorkspaceScope}
        switchWorkspace={switchWorkspace}
        switchPersonaRole={switchPersonaRole}
        signOut={signOut}
      />

      <aside
        aria-label={ADMIN_STRINGS.navigation.railAriaLabel}
        className="fixed bottom-0 left-0 right-0 z-30 flex h-[60px] flex-row items-center justify-around border-t border-border bg-surface-frosted px-2 backdrop-blur-md md:bottom-auto md:left-[10px] md:right-auto md:top-1/2 md:h-auto md:w-[68px] md:-translate-y-1/2 md:flex-col md:gap-3 md:border-none md:bg-transparent md:p-0 md:backdrop-blur-none"
      >
        <RailActionMenu
          pageContext={pageContext}
          canCreateIdentifier={canCreateIdentifier}
          canExportIdentifier={canExportIdentifier}
          canManageDomains={canManageDomains}
          canExportAuditLog={canExportAuditLog}
          onAddProduct={() => dispatchOrNavigate('mp:toggle-new-product', '/products/new')}
          onImportCsv={() => dispatchOrNavigate('mp:toggle-import-csv', '/products/import')}
          onExportCsv={() => dispatchOrNavigate('mp:export-csv', '/products/export')}
          onCheckPrintedQr={() => openQrCheckInNewTab()}
          onAddDomain={() => dispatchWindowEvent('mp:open-add-domain')}
          onExportAuditCsv={() => dispatchWindowEvent('mp:export-audit-csv')}
        />

        <div className="flex h-full w-full flex-row items-center justify-around md:h-auto md:w-[68px] md:flex-col md:gap-[5px] md:rounded-dock md:border md:border-border md:bg-surface md:p-[5px] md:shadow-card-base">
          <nav
            aria-label={ADMIN_STRINGS.navigation.primaryNavAriaLabel}
            className="flex h-full w-full flex-row items-center justify-around md:h-auto md:flex-col md:gap-[5px]"
          >
            {visibleNavItems.map((item) => {
              const hasWriteAccess =
                can(item.writeCapability) ||
                (item.specCapability ? canSpec(item.specCapability) : false);
              return (
                <NavLink
                  key={item.id}
                  to={item.to}
                  title={hasWriteAccess ? item.label : `${item.label} (Read-only)`}
                  aria-label={item.label}
                  className={({ isActive }) =>
                    `group relative flex h-full flex-1 flex-col items-center justify-center gap-[2px] transition-all md:h-[50px] md:min-h-[50px] md:w-full md:flex-none md:rounded-card md:px-0.5 ${
                      isActive
                        ? 'font-semibold text-brand md:border md:border-brand-border-tint md:bg-brand-wash'
                        : 'font-medium text-fg-secondary hover:text-fg md:border md:border-transparent md:hover:bg-surface-subtle'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        aria-hidden="true"
                        className={`absolute top-0 h-[3px] w-8 rounded-b-[3px] bg-brand transition-opacity md:hidden ${
                          isActive ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                      {NAV_ICONS[item.id]}
                      <span className="max-w-full truncate text-[11px] leading-[1.1] tracking-[-0.1px]">
                        {item.label}
                      </span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </aside>

      <main
        id="admin-main-content"
        tabIndex={-1}
        className="min-w-0 w-full max-w-full flex-1 overflow-x-clip pb-24 focus:outline-none md:pb-6 md:pl-[88px]"
      >
        <div className="min-w-0 w-full p-4 sm:p-6">
          <InsideAdminShellContext.Provider value={true}>
            <Outlet />
          </InsideAdminShellContext.Provider>
        </div>
      </main>
    </div>
  );
}

export interface AdminRoutesProps {
  readonly onSetRecall?: (payload: SetRecallCallablePayload) => Promise<SetRecallCallableResult>;
  readonly onClearRecall?: (
    payload: ClearRecallCallablePayload,
  ) => Promise<ClearRecallCallableResult>;
  readonly onExportAuditCsv?: (csvContent: string, filename: string) => void;
}

export function AdminRoutes(props?: AdminRoutesProps): ReactElement {
  useEffect(() => {
    initializeAdminTheme();
  }, []);
  const resolverHost = (
    import.meta as { readonly env?: Readonly<Record<string, string | undefined>> }
  ).env?.['VITE_RESOLVER_HOST']?.trim();
  const hostProp = resolverHost ? { resolverHost } : {};
  const productsElement = (
    <ProductsPage
      {...hostProp}
      {...(props?.onSetRecall !== undefined ? { onSetRecall: props.onSetRecall } : {})}
      {...(props?.onClearRecall !== undefined ? { onClearRecall: props.onClearRecall } : {})}
    />
  );
  const auditElement = (
    <AuditLogPage
      {...(props?.onExportAuditCsv !== undefined ? { onExportCsv: props.onExportAuditCsv } : {})}
    />
  );

  return (
    <Routes>
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/auth/action" element={<FirebaseAuthActionPage />} />
      <Route path="/qr-check" element={<QrCheckPage autoStartCamera {...hostProp} />} />
      <Route element={<AdminShellLayout />}>
        <Route index element={<Navigate to="/overview" replace />} />
        <Route path="/overview" element={<OverviewPage />} />
        <Route path="/products/*" element={productsElement} />
        <Route path="/domains" element={<SettingsPage initialTab="domains" />} />
        <Route path="/activity" element={auditElement} />
        <Route path="/audit-log" element={auditElement} />
        <Route path="/brand" element={<SettingsPage initialTab="brand-qr" />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/help/:slug" element={<HelpPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/products" replace />} />
    </Routes>
  );
}

export function App(props?: { readonly initialSession?: AdminUserSession | null }): ReactElement {
  return (
    <AuthProvider
      {...(props?.initialSession !== undefined ? { initialSession: props.initialSession } : {})}
    >
      <BrowserRouter>
        <AdminRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
